import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, Clock, Play, Square, MapPin, Car, Pencil, ChevronLeft, ChevronRight, Users, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import {
  orEmptyIfForbidden, getTimeEntries, createTimeEntry, updateTimeEntry, getProjects, getEmployees,
  punchIn, punchOut, getActiveTimeEntries,
} from '../../services/api';
import {
  businessNow, entryMinutes, formatDuration, openEntryOf, toDateKey, weekSummary,
} from '../../lib/timeclock';
import { VehicleLogCard } from '../../components/dashboard/VehicleLogCard';

const shortDate = (value) => format(new Date(`${value}T12:00:00`), 'dd MMM yyyy', { locale: fr });
const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
const errorDetail = (error, fallback) => error?.response?.data?.detail || fallback;

export const TimeEntriesPage = () => {
  const { user, can } = useAuth();
  const teamView = can('time_entries.view', 'all');
  const canClock = can('time_entries.create');
  const canEditAll = can('time_entries.edit', 'all');
  const canEditOwn = can('time_entries.edit');
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [onShift, setOnShift] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [punching, setPunching] = useState(false);
  const [punchProject, setPunchProject] = useState('');
  const [now, setNow] = useState(() => businessNow());
  const [weekOffset, setWeekOffset] = useState(0);
  const [recapEmployee, setRecapEmployee] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    date: toDateKey(businessNow()),
    start_time: '',
    end_time: '',
    project_id: '',
    mileage: 0,
    meal_expense: 0
  });

  const fetchData = useCallback(async () => {
    try {
      const [entriesRes, projectsRes, activeRes, staffRes] = await Promise.all([
        getTimeEntries(),
        orEmptyIfForbidden(getProjects()),
        teamView ? getActiveTimeEntries() : Promise.resolve({ data: [] }),
        canEditAll ? orEmptyIfForbidden(getEmployees()) : Promise.resolve({ data: [] }),
      ]);
      setEntries(entriesRes.data);
      setProjects(projectsRes.data);
      setOnShift(activeRes.data);
      setStaff((Array.isArray(staffRes.data) ? staffRes.data : []).filter((person) => person.is_active !== false));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [teamView, canEditAll]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Horloge et minuterie : rafraîchies chaque 30 secondes.
  useEffect(() => {
    const timer = setInterval(() => setNow(businessNow()), 30000);
    return () => clearInterval(timer);
  }, []);

  const activeEntry = useMemo(() => openEntryOf(entries, user?.id), [entries, user?.id]);
  const forgotten = activeEntry && activeEntry.date !== toDateKey(now);
  const recapId = recapEmployee || user?.id;
  const summary = useMemo(
    () => weekSummary(entries, recapId, now, weekOffset),
    [entries, recapId, now, weekOffset]
  );
  const recapPeople = useMemo(() => {
    const people = new Map();
    if (user?.id) people.set(user.id, `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Moi');
    entries.forEach((entry) => {
      if (!people.has(entry.employee_id)) people.set(entry.employee_id, entry.employee_name);
    });
    return Array.from(people, ([id, name]) => ({ id, name }));
  }, [entries, user]);
  const maxDayMinutes = Math.max(480, ...summary.days.map((day) => day.minutes));

  const canEditEntry = (entry) => canEditAll || (canEditOwn && entry.employee_id === user?.id);

  const handlePunchIn = async () => {
    setPunching(true);
    try {
      const { data } = await punchIn(punchProject ? { project_id: punchProject } : {});
      toast.success(`Punch in à ${data.start_time}`);
      setPunchProject('');
      fetchData();
    } catch (error) {
      toast.error(errorDetail(error, 'Erreur lors du punch in'));
    } finally {
      setPunching(false);
    }
  };

  const handlePunchOut = async () => {
    if (forgotten) {
      toast.error(`Pointage du ${shortDate(activeEntry.date)} non terminé : indiquez l'heure de fin`);
      handleEdit(activeEntry);
      return;
    }
    setPunching(true);
    try {
      const { data } = await punchOut();
      toast.success(`Punch out à ${data.end_time}`);
      fetchData();
    } catch (error) {
      toast.error(errorDetail(error, 'Erreur lors du punch out'));
      fetchData();
    } finally {
      setPunching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && editingEntry) {
        const { project_id: projectId, employee_id: ignoredEmployee, ...rest } = formData;
        await updateTimeEntry(editingEntry.id, {
          ...rest,
          end_time: formData.end_time || null,
          project_id: projectId || null,
        });
        toast.success('Entrée modifiée');
      } else {
        const { employee_id: employeeId, ...fields } = formData;
        await createTimeEntry({
          ...fields,
          ...(employeeId && employeeId !== user?.id ? { employee_id: employeeId } : {}),
          end_time: formData.end_time || null,
          project_id: formData.project_id || null,
        });
        toast.success('Entrée ajoutée');
      }
      setFormOpen(false);
      setEditMode(false);
      setEditingEntry(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(errorDetail(error, 'Erreur lors de l\'enregistrement'));
    }
  };

  const handleEdit = (entry) => {
    setEditMode(true);
    setEditingEntry(entry);
    setFormData({
      employee_id: entry.employee_id,
      date: entry.date,
      start_time: entry.start_time || '',
      end_time: entry.end_time || '',
      project_id: entry.project_id || '',
      mileage: entry.mileage || 0,
      meal_expense: entry.meal_expense || 0
    });
    setFormOpen(true);
  };

  const resetForm = (employeeId = '') => {
    setFormData({
      employee_id: employeeId,
      date: toDateKey(businessNow()),
      start_time: '',
      end_time: '',
      project_id: '',
      mileage: 0,
      meal_expense: 0
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-entries-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a3a5c]">Horodatage</h1>
          <p className="text-slate-500">Pointez vos heures et consultez votre récapitulatif</p>
        </div>
        {(canClock || canEditAll) && (
          <Button
            onClick={() => {
              resetForm(canClock ? '' : staff[0]?.id || '');
              setEditMode(false);
              setEditingEntry(null);
              setFormOpen(true);
            }}
            variant="outline"
            className="rounded-full"
            data-testid="add-entry-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Entrée manuelle
          </Button>
        )}
      </div>

      <div className={`grid grid-cols-1 gap-6 ${canClock && teamView ? 'lg:grid-cols-2' : ''}`}>
        {/* Punch in / out */}
        {canClock && (
          <Card className={activeEntry ? (forgotten ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50') : ''}>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <button
                  type="button"
                  onClick={activeEntry ? handlePunchOut : handlePunchIn}
                  disabled={punching}
                  className={`shrink-0 w-36 h-36 rounded-full text-white shadow-lg flex flex-col items-center justify-center gap-2 transition-transform active:scale-95 disabled:opacity-60 ${
                    activeEntry ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'
                  }`}
                  data-testid={activeEntry ? 'punch-out-btn' : 'punch-in-btn'}
                >
                  {activeEntry ? <Square className="w-9 h-9" /> : <Play className="w-9 h-9" />}
                  <span className="text-lg font-bold">{activeEntry ? 'Punch out' : 'Punch in'}</span>
                </button>
                <div className="flex-1 w-full text-center sm:text-left space-y-3">
                  <p className="text-4xl font-bold text-[#1a3a5c]" data-testid="punch-clock">
                    {format(now, 'HH:mm')}
                  </p>
                  <p className="text-slate-500">{capitalize(format(now, 'EEEE d MMMM', { locale: fr }))}</p>
                  {activeEntry ? (
                    forgotten ? (
                      <p className="flex items-center justify-center sm:justify-start gap-2 text-amber-800 font-medium">
                        <AlertTriangle className="w-4 h-4" />
                        Punch out oublié le {shortDate(activeEntry.date)} : corrigez l'heure de fin
                      </p>
                    ) : (
                      <div className="flex items-center justify-center sm:justify-start gap-2 text-green-800">
                        <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                        <span className="font-medium" data-testid="punch-status">
                          En poste depuis {activeEntry.start_time} · {formatDuration(entryMinutes(activeEntry, now))}
                        </span>
                      </div>
                    )
                  ) : (
                    projects.length > 0 && (
                      <Select
                        value={punchProject || 'none'}
                        onValueChange={(value) => setPunchProject(value === 'none' ? '' : value)}
                      >
                        <SelectTrigger className="max-w-xs mx-auto sm:mx-0" data-testid="punch-project">
                          <SelectValue placeholder="Chantier (optionnel)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sans chantier</SelectItem>
                          {projects.map((project) => (
                            <SelectItem key={project.id} value={project.id}>{project.client_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )
                  )}
                  {activeEntry?.project_name && !forgotten && (
                    <p className="text-sm text-slate-600">Chantier : {activeEntry.project_name}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personnes en poste */}
        {teamView && (
          <Card data-testid="on-shift-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-[#00b4d8]" />
                En poste maintenant
                <span className="ml-auto text-sm font-normal text-slate-500">{onShift.length}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {onShift.length === 0 ? (
                <p className="text-slate-500 text-sm py-4 text-center">Personne n'est pointé présentement</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {onShift.map((entry) => {
                    const stale = entry.date !== toDateKey(now);
                    return (
                      <li key={entry.id} className="py-2 flex items-center gap-3">
                        <span className={`w-2.5 h-2.5 rounded-full ${stale ? 'bg-amber-500' : 'bg-green-500'}`}></span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#0f172a] truncate">{entry.employee_name}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {stale ? `Punch out oublié le ${shortDate(entry.date)}` : `Depuis ${entry.start_time}`}
                            {entry.project_name ? ` · ${entry.project_name}` : ''}
                          </p>
                        </div>
                        {!stale && (
                          <span className="text-sm font-medium text-[#1a3a5c]">
                            {formatDuration(entryMinutes(entry, now))}
                          </span>
                        )}
                        {canEditAll && (
                          <Button
                            size="sm"
                            variant={stale ? 'default' : 'outline'}
                            className={stale ? 'bg-amber-500 hover:bg-amber-600 h-8' : 'h-8'}
                            onClick={() => handleEdit(entry)}
                            data-testid={`fix-shift-${entry.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" />
                            {stale ? 'Corriger' : 'Modifier'}
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Récapitulatif hebdomadaire */}
      <Card data-testid="week-recap">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <CardTitle className="text-lg">Récapitulatif</CardTitle>
            {teamView && recapPeople.length > 1 && (
              <Select value={recapId || ''} onValueChange={setRecapEmployee}>
                <SelectTrigger className="sm:w-56" data-testid="recap-employee">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {recapPeople.map((person) => (
                    <SelectItem key={person.id} value={person.id}>{person.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex items-center gap-1 sm:ml-auto">
              <Button size="sm" variant="ghost" onClick={() => setWeekOffset(weekOffset - 1)} aria-label="Semaine précédente">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600 min-w-[11rem] text-center">
                {weekOffset === 0 ? 'Cette semaine' : `${shortDate(summary.from)} – ${shortDate(summary.to)}`}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setWeekOffset(weekOffset + 1)}
                disabled={weekOffset >= 0}
                aria-label="Semaine suivante"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-7 gap-2 items-end h-36">
            {summary.days.map((day) => (
              <div key={day.date} className="flex flex-col items-center justify-end h-full gap-1">
                <span className="text-xs font-medium text-[#1a3a5c]">
                  {day.minutes ? formatDuration(day.minutes).replace(' h ', 'h') : ''}
                </span>
                <div
                  className={`w-full max-w-[2.5rem] rounded-t-md ${day.date === toDateKey(now) ? 'bg-[#00b4d8]' : 'bg-[#1a3a5c]/70'}`}
                  style={{ height: `${Math.max(day.minutes ? 4 : 0, (day.minutes / maxDayMinutes) * 100)}%` }}
                ></div>
                <span className="text-xs text-slate-500">{day.label}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a3a5c]" data-testid="week-total">{formatDuration(summary.minutes)}</p>
                <p className="text-sm text-slate-500">Heures</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Car className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a3a5c]">{summary.mileage} km</p>
                <p className="text-sm text-slate-500">Kilométrage</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1a3a5c]">{summary.meals.toFixed(2)} $</p>
                <p className="text-sm text-slate-500">Frais de repas</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <VehicleLogCard userId={user?.id} canRecord={canClock} teamView={teamView} people={recapPeople} />

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Historique</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {teamView && <TableHead>Employé</TableHead>}
                  <TableHead>Date</TableHead>
                  <TableHead>Début</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead className="text-right">Durée</TableHead>
                  <TableHead>Projet</TableHead>
                  <TableHead className="text-right">Km</TableHead>
                  <TableHead className="text-right">Repas</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.slice(0, 50).map((entry) => (
                  <TableRow key={entry.id}>
                    {teamView && <TableCell>{entry.employee_name}</TableCell>}
                    <TableCell className="whitespace-nowrap">
                      {shortDate(entry.date)}
                      {entry.corrected && (
                        <span className="ml-2 text-xs rounded-full bg-amber-100 text-amber-800 px-2 py-0.5" title={entry.corrected_by_name ? `Corrigé par ${entry.corrected_by_name}` : "Heures pointées corrigées à la main"}>
                          Corrigé
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{entry.start_time}</TableCell>
                    <TableCell>{entry.end_time || <span className="text-green-700">en cours</span>}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {entry.end_time ? formatDuration(entryMinutes(entry, now)) : '-'}
                    </TableCell>
                    <TableCell>{entry.project_name || '-'}</TableCell>
                    <TableCell className="text-right">{entry.mileage || 0}</TableCell>
                    <TableCell className="text-right">{(entry.meal_expense || 0).toFixed(2)} $</TableCell>
                    <TableCell className="text-center">
                      {canEditEntry(entry) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(entry)}
                          className="h-8 w-8 p-0 hover:bg-slate-100"
                          data-testid={`edit-entry-${entry.id}`}
                        >
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {entries.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <p>Aucune entrée</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={formOpen} onOpenChange={(open) => {
        setFormOpen(open);
        if (!open) {
          setEditMode(false);
          setEditingEntry(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editMode ? 'Modifier l\'entrée' : 'Ajouter une entrée'}
              {editMode && editingEntry && editingEntry.employee_id !== user?.id && (
                <span className="block text-sm font-normal text-slate-500 mt-1">{editingEntry.employee_name}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editMode && canEditAll && staff.length > 0 && (
              <div>
                <Label>Employé *</Label>
                <Select
                  value={formData.employee_id || user?.id || ''}
                  onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                >
                  <SelectTrigger data-testid="entry-employee">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((person) => (
                      <SelectItem key={person.id} value={person.id}>
                        {person.first_name} {person.last_name}{person.id === user?.id ? ' (moi)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Pour un employé qui a oublié de puncher.</p>
              </div>
            )}
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Heure de début *</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Heure de fin</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Projet</Label>
              <Select 
                value={formData.project_id || "none"} 
                onValueChange={(value) => setFormData({ ...formData, project_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un projet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.client_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kilométrage</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.mileage}
                  onChange={(e) => setFormData({ ...formData, mileage: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Frais de repas ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.meal_expense}
                  onChange={(e) => setFormData({ ...formData, meal_expense: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#00b4d8] hover:bg-[#009bb8]">
                {editMode ? 'Enregistrer' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeEntriesPage;
